'use client';

import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';

import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type SwiperCore from 'swiper'; // Use import type
import { Autoplay, Navigation, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import { dashboardUrl } from '@/src/utils';

interface FeatureSlide {
  title: string;
  image: string;
  alt: string;
  desc: string;
}

interface ICarouselProps {
  isShowDescription: boolean;
}

export const FeatureCarousel = ({
  isShowDescription = false,
}: ICarouselProps) => {
  const { t } = useTranslation();

  const swiperRef = useRef<SwiperCore | null>(null);

  const features: FeatureSlide[] = [
    {
      title: 'IEP Goal Tracking App',
      image: '/images/blog/iep-goal-tracking.svg',
      alt: 'IEP goal tracking interface with progress metrics',
      desc: 'Accurately log and track service minutes with automated real-time documentation.',
    },
    {
      title: 'Service Minutes Tracking',
      image: '/images/blog/service-min.svg',
      alt: 'Service minutes tracking interface showing service time selection',
      desc: 'Track student progress effortlessly with automated data collection and real-time insights.',
    },
    {
      title: 'AI-Powered Progress Notes',
      image: '/images/blog/progress-notes.svg',
      alt: 'AI-powered progress notes interface with generation button',
      desc: 'Create detailed progress notes instantly with AI-generated insights and automation.',
    },
    {
      title: 'Accommodations Tracking',
      image: '/images/blog/accommodations-tracking.svg',
      alt: 'Accommodations tracking interface with options',
      desc: 'Monitor and document student accommodations seamlessly with smart tracking tools.',
    },
  ];

  return (
    <div
      className={`${
        isShowDescription ? 'sticky top-20' : ''
      } mx-auto w-full max-w-[300px] md:max-w-[360px] lg:max-w-[409px]`}
      onMouseEnter={() =>
        swiperRef.current && swiperRef.current.autoplay.stop()
      }
      onMouseLeave={() =>
        swiperRef.current && swiperRef.current?.autoplay.start()
      }
    >
      <Swiper
        modules={[Pagination, Autoplay, Navigation]}
        loop={true}
        autoplay={{
          delay: 3000,
          disableOnInteraction: false,
        }}
        pagination={{
          clickable: true,
          renderBullet: (index: number, className: string) => {
            return `<span class="${className} ${
              index === swiperRef?.current?.realIndex
                ? '!bg-gray-800'
                : '!bg-gray-500'
            } w-3 h-3 rounded-full mx-1"></span>`;
          },
        }}
        className="overflow-hidden"
        onSwiper={(swiper: any) => {
          swiperRef.current = swiper;
        }}
      >
        {features.map((feature, index) => (
          <SwiperSlide key={index}>
            <div
              className={`rounded-md border border-[#F1F2F0] bg-[#FCFEFB] pb-12 pt-6 ${
                isShowDescription ? 'mt-6' : ''
              }`}
            >
              <div className="flex flex-col items-center">
                <div className="flex h-auto w-full max-w-[409px] items-center justify-center overflow-hidden rounded-lg">
                  <img
                    src={feature.image}
                    alt={feature.alt}
                    className="h-[303px] w-full max-w-[361px] object-contain px-4 py-3"
                  />
                </div>
                <h3 className="-mt-12 mb-4 text-center font-rubik text-2xl font-medium leading-[130%] tracking-[0] text-[#464E5F] sm:text-2xl">
                  {t(feature.title)}
                </h3>
                {isShowDescription && (
                  <p className="mb-4 h-[63px] w-[276px] text-center font-rubik text-sm font-normal leading-[150%] tracking-[0%] text-[#6B717F]">
                    {t(feature.desc)}
                  </p>
                )}
                <a
                  href={`${dashboardUrl}/signup`}
                  className="flex h-[52px] w-[177px] items-center justify-center gap-[8px] rounded-lg bg-gradient-to-b from-[#49CF95] to-[#2FB77C] font-rubik text-base font-medium text-white transition-opacity hover:opacity-90 sm:text-lg"
                >
                  {t('Sign Up for Free')}
                </a>
              </div>
            </div>
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  );
};

export const BlogHeroSection = () => {
  const { t } = useTranslation();
  return (
    <div className="mx-auto flex w-full max-w-[1166px] flex-col px-4 sm:mt-[120px] sm:px-6 md:flex-row">
      <div className="mt-6 flex w-full flex-col justify-center gap-2 md:mt-0 md:w-[736px] lg:mt-0 lg:w-[736px]">
        <p className="font-rubik text-sm font-medium text-[#479F60]">
          {t('BLOGS')}
        </p>
        <p className="font-rubik text-3xl font-medium leading-[125%] tracking-[-0.02em] text-[#464E5F] sm:text-4xl md:text-5xl lg:text-[52px]">
          {t('Empowering Special Education with Smart Solutions!')}
        </p>
        <p className="font-rubik text-lg font-normal leading-[150%] tracking-[0%] text-[#6B717F] sm:text-xl">
          {t(' Hundreds of blog with great solutions')}
        </p>
      </div>
      <div className="mt-6 flex w-full justify-center md:mt-0 md:w-[409px] lg:mt-0 lg:w-[409px]">
        <FeatureCarousel isShowDescription={false} />
      </div>
    </div>
  );
};
